import React, { useState } from 'react';
import { Modal, Form, Alert, ListGroup, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileEdit, faDownload, faFilePdf, faFileCode } from '@fortawesome/free-solid-svg-icons';
import { useToast } from 'providers/ToastProvider';
import Button from 'components/base/Button';
import { useEventosNFe, useEmitirCartaCorrecaoNFe } from '../../../../hooks/useNFe';
import * as nfeService from '../../../../services/nfeService';
import { formatDateTimeBrazil } from '../../../../utils/dateUtils';

const TIPO_CARTA_CORRECAO = 110110;

interface BotaoCartaCorrecaoNFeProps {
  emissaoId: string;
}

const BotaoCartaCorrecaoNFe: React.FC<BotaoCartaCorrecaoNFeProps> = ({ emissaoId }) => {
  const [show, setShow] = useState(false);
  const [correcao, setCorrecao] = useState('');
  const [erro, setErro] = useState('');

  const { showToast } = useToast();

  const eventosQuery = useEventosNFe(emissaoId, show);
  const cces = (eventosQuery.data ?? []).filter(e => e.tipoEvento === TIPO_CARTA_CORRECAO);

  const emitirMutation = useEmitirCartaCorrecaoNFe(emissaoId);

  const handleAbrir = () => {
    setShow(true);
    setCorrecao('');
    setErro('');
  };

  const handleFechar = () => {
    if (!emitirMutation.isPending) setShow(false);
  };

  const handleConfirmar = () => {
    if (correcao.length < 15) {
      setErro('Correção deve ter pelo menos 15 caracteres');
      return;
    }
    setErro('');
    emitirMutation.mutate(
      { correcao },
      {
        onSuccess: (data) => {
          showToast(`CC-e #${data.nSeqEvento} emitida. Protocolo: ${data.protocolo}`, 'success');
          setCorrecao('');
        },
        onError: (error: any) => {
          setErro(error?.response?.data?.message ?? 'Erro ao emitir Carta de Correção');
        },
      }
    );
  };

  const downloadArquivo = async (promiseFn: Promise<Blob>, fileName: string) => {
    const blob = await promiseFn;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Button variant="phoenix-warning" size="sm" onClick={handleAbrir}>
        <FontAwesomeIcon icon={faFileEdit} className="me-1" /> Carta de Correção
      </Button>

      <Modal show={show} onHide={handleFechar} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Carta de Correção Eletrônica (CC-e)</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Alert variant="info" className="border border-info border-opacity-25 bg-info-subtle mb-3">
            <div className="text-info-emphasis small">
              Use a CC-e para corrigir erros que não afetam valores fiscais, destinatário ou data de emissão.
              Múltiplas correções são permitidas — cada envio cria uma nova sequência.
            </div>
          </Alert>

          {cces.length > 0 && (
            <div className="mb-4">
              <h6 className="text-body-emphasis mb-2">Correções anteriores</h6>
              <ListGroup variant="flush" className="border border-opacity-25 rounded">
                {cces.map((cce) => (
                  <ListGroup.Item key={cce.id} className="px-3 py-2">
                    <div className="d-flex align-items-start justify-content-between gap-2">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <Badge bg={cce.resultado === 'A' ? 'success' : 'danger'} className="font-monospace">
                            #{cce.nSeqEvento.toString().padStart(2, '0')}
                          </Badge>
                          <span className="text-body-secondary small">
                            {cce.dhRecbto ? formatDateTimeBrazil(cce.dhRecbto) : formatDateTimeBrazil(cce.dhTentativa)}
                          </span>
                          {cce.protocolo && (
                            <span className="font-monospace text-body-secondary" style={{ fontSize: '0.7rem' }}>
                              {cce.protocolo}
                            </span>
                          )}
                        </div>
                        <p className="mb-0 small text-body-emphasis">{cce.justificativa}</p>
                        {cce.xMotivo && cce.resultado !== 'A' && (
                          <p className="mb-0 small text-danger-emphasis mt-1">{cce.cStat} — {cce.xMotivo}</p>
                        )}
                      </div>
                      {cce.resultado === 'A' && (
                        <div className="d-flex gap-1 flex-shrink-0">
                          <Button
                            variant="phoenix-secondary"
                            size="sm"
                            onClick={() => downloadArquivo(
                              nfeService.downloadPdfCCe(emissaoId, cce.id),
                              `cce_${cce.nSeqEvento.toString().padStart(2, '0')}.pdf`
                            )}
                            title="Baixar PDF da CC-e"
                          >
                            <FontAwesomeIcon icon={faFilePdf} />
                          </Button>
                          <Button
                            variant="phoenix-secondary"
                            size="sm"
                            onClick={() => downloadArquivo(
                              nfeService.downloadXmlEvento(emissaoId, cce.id),
                              `cce_${cce.nSeqEvento.toString().padStart(2, '0')}.xml`
                            )}
                            title="Baixar XML da CC-e"
                          >
                            <FontAwesomeIcon icon={faFileCode} />
                          </Button>
                        </div>
                      )}
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}

          <Form.Group>
            <Form.Label>Nova correção *</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={correcao}
              onChange={(e) => setCorrecao(e.target.value)}
              placeholder="Descreva a correção (mínimo 15, máximo 1000 caracteres). Ex: 'Onde se lê CFOP 5102, leia-se CFOP 5405.'"
              maxLength={1000}
              isInvalid={!!erro}
              disabled={emitirMutation.isPending}
            />
            <div className="d-flex justify-content-between">
              <Form.Text className="text-body-secondary">{correcao.length}/1000 caracteres</Form.Text>
            </div>
            {erro && <Form.Control.Feedback type="invalid">{erro}</Form.Control.Feedback>}
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="subtle-secondary" onClick={handleFechar} disabled={emitirMutation.isPending}>
            Fechar
          </Button>
          <Button
            variant="warning"
            onClick={handleConfirmar}
            loading={emitirMutation.isPending}
          >
            Emitir CC-e
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BotaoCartaCorrecaoNFe;
